import { HttpService } from "@app/core/services/http/http.service";
import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AppInjector } from "@app/core/functions/app-injector";
import { AuthenticationService } from "@app/core/services/authentication.service";
import { PortalSettingsService } from "@app/core/services/portal-settings.service";
import { IPLUS_URL } from "@app/shared/constants/url.constants";
import { InputSettings, NotificationSettings, TooltipSettings, ContextMenuSettings } from "@app/shared/models/portal-settings.model";
import {
  FavoritesSettings,
  InBasketSettings,
  OndemandSettings,
  RecentActivitySettings,
  ReportSettings,
  SideNavSettings,
  TableSettings,
  TabSettings,
  ThemeSettings,
  UserSettings,
  UserSettingsAdapter,
  ViewerSettings
} from "@app/shared/models/user-settings.model";
import { exhaustMap, map, of, tap } from "rxjs";
import { ROUTES } from "@app/shared/constants/routes.constants";

@Injectable({ providedIn: "root" })
export class UserSettingsService {
  private http = inject(HttpService);
  private routerService = inject(Router);
  private _userSettings: UserSettings;

  /**
   * Load user settings and set portal options
   * @returns
   */
  loadSettings() {
    const respAdapter: UserSettingsAdapter = new UserSettingsAdapter();
    return of("").pipe(
      exhaustMap(() => {
        return this.http.get<UserSettings>(IPLUS_URL.USER_SETTINGS).pipe(
          map((response: any) => respAdapter.adapt(response)),
          tap((userSettings) => {
            // Ignore house accounts that are assuming disabled accounts.
            if (!userSettings.isActive && !this.authService.isHouseAccount && !this.authService.activeAccount.credentials.isIdentityAssumed) {
              this.routerService.navigate([ROUTES.LOGIN]);
              return;
            }

            this._userSettings = userSettings;
            this.portalSettingService.setPortalOptions();
          })
        );
      })
    );
  }

  /**
   * Clear user settings and set portal options.
   */
  reset() {
    this._userSettings = undefined;
    this.portalSettingService.setPortalOptions();
  }

  get userSettings(): UserSettings {
    return this._userSettings;
  }

  /**
   * Get language value
   */
  get language(): string {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.language;
  }

  /**
   * Set language value
   */
  set language(value: string) {
    if (this._userSettings) {
      this._userSettings.language = value;
    }
  }

  /**
   * Get document viewer settings
   */
  get viewerSettings(): ViewerSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.viewerSettings;
  }

  /**
   * Sets document viewer settings
   */
  set viewerSettings(settings: ViewerSettings) {
    if (this._userSettings) {
      this._userSettings.viewerSettings = settings;
    }
  }

  /**
   * Gets sidenav settings.
   */
  get sideNavSettings(): SideNavSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.sideNavSettings;
  }

  /**
   * Set sidenav settings
   */
  set sideNavSettings(settings: SideNavSettings) {
    if (this._userSettings) {
      this._userSettings.sideNavSettings = settings;
    }
  }

  /**
   * Gets Favorites settings.
   */
  get favoritesSettings(): FavoritesSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.favoritesSettings;
  }

  /**
   * Set Favorites settings
   */
  set favoritesSettings(settings: FavoritesSettings) {
    if (this._userSettings) {
      this._userSettings.favoritesSettings = settings;
    }
  }

  /**
   * Get My Reports settings
   */
  get reportSettings(): ReportSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }

    return this._userSettings?.reportSettings;
  }

  /**
   * Set My Reports settings
   */
  set reportSettings(settings: ReportSettings) {
    if (this._userSettings) {
      this._userSettings.reportSettings = settings;
    }
  }

  /**
   * Get InBasket settings
   */
  get inbasketSettings(): InBasketSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }

    return this._userSettings?.inbasketSettings;
  }

  /**
   * Set InBasket settings
   */
  set inbasketSettings(settings: InBasketSettings) {
    if (this._userSettings) {
      this._userSettings.inbasketSettings = settings;
    }
  }

  /**
   * Get tab settings
   */
  get tabSettings(): TabSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.tabSettings;
  }

  /**
   * Set tab settings
   */
  set tabSettings(settings: TabSettings) {
    if (this._userSettings) {
      this._userSettings.tabSettings = settings;
    }
  }

  /**
   * Get ondemand settings
   */
  get ondemandSettings(): OndemandSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }

    return this._userSettings?.ondemandSettings;
  }

  /**
   * Set ondemand settings
   */
  set ondemandSettings(settings: OndemandSettings) {
    if (this._userSettings) {
      this._userSettings.ondemandSettings = settings;
    }
  }

  /**
   * Get table settings
   */
  get tableSettings(): TableSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }

    return this._userSettings?.tableSettings;
  }

  /**
   * Set table settings
   */
  set tableSettings(settings: TableSettings) {
    if (this._userSettings) {
      this._userSettings.tableSettings = settings;
    }
  }

  /**
   * Get theme once logged in
   */
  get theme(): ThemeSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }

    return this._userSettings?.themeSettings;
  }

  /**
   * Set user theme
   */
  set theme(theme: ThemeSettings) {
    if (this._userSettings) {
      this._userSettings.themeSettings = theme;
    }
  }

  get inputSettings(): InputSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.inputSettings;
  }

  set inputSettings(settings: InputSettings) {
    if (this._userSettings) {
      this._userSettings.inputSettings = settings;
    }
  }

  /**
   * Get notification settings.
   */
  get notificationSettings(): NotificationSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.notificationSettings;
  }

  /**
   * Updates notification settings
   */
  set notificationSettings(settings: NotificationSettings) {
    if (this._userSettings) {
      this._userSettings.notificationSettings = settings;
    }
  }

  /**
   * Get tooltip settings.
   */
  get tooltipSettings(): TooltipSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.tooltipSettings;
  }

  /**
   * Updates tooltip settings
   */
  set tooltipSettings(settings: TooltipSettings) {
    if (this._userSettings) {
      this._userSettings.tooltipSettings = settings;
    }
  }

  /**
   * Get recent activity settings.
   */
  get recentActivitySettings(): RecentActivitySettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.recentActivity;
  }

  /**
   * Updates recent activity settings
   */
  set recentActivitySettings(settings: RecentActivitySettings) {
    if (this._userSettings) {
      this._userSettings.recentActivity = settings;
    }
  }

  /**
   * Get contextMenuSettings settings.
   */
  get contextMenuSettings(): ContextMenuSettings {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.contextMenuSettings;
  }

  /**
   * Updates contextMenuSettings settings
   */
  set contextMenuSettings(settings: ContextMenuSettings) {
    if (this._userSettings) {
      this._userSettings.contextMenuSettings = settings;
    }
  }

  /**
   * Get hints and tips settings.
   */
  get isTipsAndHintsEnabled(): boolean {
    if (!this.authService.isAuthenticated()) {
      return null;
    }
    return this._userSettings?.isTipsAndHintsEnabled;
  }

  /**
   * Updates hints and tips settings.
   */
  set isTipsAndHintsEnabled(enabled: boolean) {
    if (this._userSettings) {
      this._userSettings.isTipsAndHintsEnabled = enabled;
    }
  }

  get authService(): AuthenticationService {
    return AppInjector.get(AuthenticationService);
  }

  get portalSettingService(): PortalSettingsService {
    return AppInjector.get(PortalSettingsService);
  }
}
